import { useEffect, useRef, useState } from 'react';

import { RouterProvider } from '@tanstack/react-router';

import { registerAppModules } from '@bootstrap/app-registry';
import { useAuthClient } from '@bootstrap/auth/context';
import { APButton, APTypography } from '@shared/components';
import APLoading from '@shared/components/molecules/loading';
import { useTranslate } from '@shared/hooks/use-translate';
import { PermissionsService } from '@shared/services/permissions/permissions.service';

import type { TPermissions } from '@modules/core/types';

import { RouterContext } from '../../router/context';
import { createAppRouter } from '../../router/route-tree';

interface AppRouterProviderProps {
  children?: React.ReactNode;
  userPermissions?: TPermissions;
}

const DEFAULT_PERMISSIONS: TPermissions = { roles: [] };

// Initialize app modules first
await registerAppModules();

/**
 * App Router Provider
 *
 * This component initializes the router with the AppModuleRegistry
 * and provides routing functionality to the entire application.
 */
export const AppRouterProvider: React.FC<AppRouterProviderProps> = ({
  userPermissions = DEFAULT_PERMISSIONS,
}) => {
  const [router, setRouter] = useState<ReturnType<typeof createAppRouter> | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedPermissions, setResolvedPermissions] = useState<TPermissions>(userPermissions);
  const authClient = useAuthClient();
  const translate = useTranslate();
  const routerCreatedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadAndInit = async () => {
      let scopes: string[] = [];
      if (authClient.snapshot.status === 'authenticated') {
        try {
          scopes = await PermissionsService.fetchMyModules();
        } catch {
          scopes = [];
        }
      }

      if (cancelled) return;

      const permissions: TPermissions = { ...userPermissions, scopes };
      setResolvedPermissions(permissions);

      if (!routerCreatedRef.current) {
        try {
          const routerInstance = createAppRouter(permissions, authClient);
          setRouter(routerInstance);
          setIsReady(true);
          routerCreatedRef.current = true;
        } catch (err) {
          console.error('Failed to initialize router:', err);
          setError(err instanceof Error ? err.message : 'Failed to initialize router');
        }
      }
    };

    loadAndInit();

    const unsubscribe = authClient.on('auth:change', () => {
      if (authClient.snapshot.status === 'authenticated') {
        PermissionsService.clearCache();
        PermissionsService.fetchMyModules()
          .then((newScopes) => {
            if (!cancelled) {
              setResolvedPermissions({ ...userPermissions, scopes: newScopes });
            }
          })
          .catch(() => {});
      } else {
        PermissionsService.clearCache();
        if (!cancelled) {
          setResolvedPermissions({ ...userPermissions, scopes: [] });
        }
      }
    });

    return () => {
      cancelled = true;
      if ('unsubscribe' in unsubscribe && typeof unsubscribe.unsubscribe === 'function') {
        unsubscribe.unsubscribe();
      }
    };
  }, [authClient, userPermissions]);

  // Loading state
  if (!isReady && !error) {
    return <APLoading />;
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: '16px',
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <APTypography as="h2" size="xLarge" weight="500">
          {translate('errors.router.title')}
        </APTypography>
        <APTypography size="base" color="error">
          {error}
        </APTypography>
        <APButton onClick={() => window.location.reload()}>{translate('buttons.retry')}</APButton>
      </div>
    );
  }

  // Router ready
  if (!router) {
    return null;
  }

  return (
    <RouterContext.Provider value={{ isReady, userPermissions: resolvedPermissions }}>
      <RouterProvider router={router} />
    </RouterContext.Provider>
  );
};

export default AppRouterProvider;
